import React, { Fragment, useEffect, useMemo, useState } from "react";

import { Dialog, Transition } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import SendAppMessageModal from "../components/SendAppMessageModal";
import { useAuth } from "../contexts/AuthContext";
import apiService from "../services/api";
import { RESELLER_SUBSCRIPTION_TICKET_URL } from "../utils/ticketIntents";

export const UPGRADE_MODAL_COPY =
  "This is a feature for resellers using our subscription based reseller " +
  "pricing plan. If you would like this feature, please contact us or " +
  "create a support ticket discussing your interest in upgrading your " +
  "experience.";

/**
 * Full App Messages access: admins and ACTIVE module resellers. Regular
 * resellers admitted by the route get the disabled marketing preview. Mirrors
 * `requireAdminOrService` on the API, which is the real security boundary.
 */
export function hasFullAppMessagesAccess(user) {
  if (user?.role === "admin") return true;

  return (
    user?.type === "Reseller" &&
    user?.resellerType === "module" &&
    user?.resellerStatus === "ACTIVE"
  );
}

const ORIGIN_PILLS = [
  { key: "all", label: "All" },
  { key: "admin", label: "Admin" },
  { key: "reseller", label: "Reseller" },
  { key: "service", label: "Service" },
];

const SEVERITY_PILLS = [
  { key: "all", label: "All", dot: "" },
  { key: "info", label: "Info", dot: "bg-sky-500" },
  { key: "warning", label: "Warning", dot: "bg-amber-500" },
  { key: "critical", label: "Critical", dot: "bg-red-500" },
];

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "active", label: "Active" },
  { value: "expired", label: "Expired" },
];

const PAGE_SIZE = 20;

/**
 * Formats a target (type + provider_domain + target_user_id) into a compact
 * human-readable label for the table. Keeps the "Target" column short -
 * long UUIDs get truncated for direct_playlist rows.
 */
function formatTarget(msg) {
  switch (msg.target_type) {
    case "global":
      return msg.provider_domain ? `Global - ${msg.provider_domain}` : "Global";
    case "provider_domain":
      return `Provider - ${msg.provider_domain}`;
    case "direct_playlist":
      return `User - ${(msg.target_user_id || "").slice(0, 8)}...`;
    case "direct_account":
      return `Account - ${(msg.target_account_id || "").slice(0, 8)}...`;
    default:
      return msg.target_type || "-";
  }
}

function severityBadge(severity) {
  const cfg =
    {
      info: "bg-sky-500/10 text-sky-400 ring-sky-500/20",
      warning: "bg-amber-500/10 text-amber-400 ring-amber-500/20",
      critical: "bg-red-500/10 text-red-400 ring-red-500/20",
    }[severity] || "bg-gray-500/10 text-gray-400 ring-gray-500/20";

  return `inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${cfg}`;
}

function originBadge(type) {
  const cfg =
    {
      admin: "bg-purple-500/10 text-purple-400 ring-purple-500/20",
      reseller: "bg-emerald-500/10 text-emerald-400 ring-emerald-500/20",
      service: "bg-gray-500/10 text-gray-400 ring-gray-500/20",
    }[type] || "bg-gray-500/10 text-gray-400 ring-gray-500/20";

  return `inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${cfg}`;
}

function formatDateTime(iso) {
  if (!iso) return "-";
  const d = new Date(iso);

  if (Number.isNaN(d.getTime())) return "-";

  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  });
}

const AppMessages = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Role gates the UI; the API is the real security boundary. Hiding
  // admin-only controls avoids rendering things the reseller would just
  // get 403s on, but the server remains authoritative.
  const isAdmin = user?.role === "admin";
  // Regular resellers see the page as a disabled marketing preview: no data
  // calls, no working actions, and an upgrade modal on entry. The API rejects
  // their tokens regardless.
  const isPreview = !hasFullAppMessagesAccess(user);

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    pages: 1,
  });
  const [loading, setLoading] = useState(!isPreview);
  const [origin, setOrigin] = useState("all");
  const [severity, setSeverity] = useState("all");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(isPreview);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteInFlight, setDeleteInFlight] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);

    return () => clearTimeout(t);
  }, [search]);

  // Any filter change resets to page 1 so the user isn't stranded on an
  // empty offset when the result set shrinks under their feet.
  useEffect(() => {
    setPage(1);
  }, [origin, severity, status, debouncedSearch]);

  const queryParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      origin: origin === "all" ? undefined : origin,
      targetType: undefined,
      status: status || undefined,
      q: debouncedSearch || undefined,
      // Severity isn't a server-side filter yet; we apply it client-side below.
    }),
    [page, origin, status, debouncedSearch]
  );

  const load = async () => {
    setLoading(true);
    try {
      const resp = await apiService.getAppMessages(queryParams);

      setRows(resp.rows || []);
      setPagination(
        resp.pagination || { page: 1, limit: PAGE_SIZE, total: 0, pages: 1 }
      );
    } catch (err) {
      toast.error(err?.message || "Failed to load app messages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Preview mode never fetches - the API would 403 these tokens anyway.
    if (isPreview) return;
    load();
    // Depending on queryParams keeps the fetch in sync with user input;
    // `load` is stable enough not to need extra memoization.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams, isPreview]);

  // Severity is applied client-side on the current page. Accurate enough
  // for a table of 20 rows; if we later add server-side severity filtering
  // (requires an API tweak) we can drop this map.
  const visibleRows = useMemo(() => {
    if (severity === "all") return rows;

    return rows.filter((r) => r.severity === severity);
  }, [rows, severity]);

  const handlePublished = () => {
    setShowSendModal(false);
    toast.success("Message published");
    // Reset to page 1 so the newly created row is visible at the top.
    if (page !== 1) setPage(1);
    else load();
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteInFlight(true);
    try {
      await apiService.deleteAppMessage(deleteTarget.id);
      toast.success("Message deleted");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err?.message || "Failed to delete message");
    } finally {
      setDeleteInFlight(false);
    }
  };

  return (
    <div className="p-4 sm:p-6">
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-2xl mb-4 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        {/* Atmosphere */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(600px 220px at 85% -20%, rgba(139, 92, 246, 0.22), transparent 60%), radial-gradient(500px 200px at 10% 120%, rgba(34, 211, 238, 0.12), transparent 60%)",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(139, 92, 246, 0.6), transparent)",
          }}
        />

        <div className="relative p-5 sm:p-7 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5">
          <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center flex-shrink-0 shadow-lg shadow-primary-500/30 ring-1 ring-white/10">
              <i className="fas fa-bullhorn text-white text-base sm:text-lg" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mb-1">
                <span className="font-mono text-[10px] font-semibold tracking-[0.16em] uppercase text-primary-600 dark:text-primary-300">
                  Broadcast Channel
                </span>
                {pagination.total > 0 && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono tracking-wider uppercase text-gray-400 dark:text-gray-500">
                    <span className="w-1 h-1 rounded-full bg-gray-400 dark:bg-gray-500" />
                    {pagination.total} total
                  </span>
                )}
              </div>
              <h1 className="text-xl sm:text-[26px] font-semibold text-gray-900 dark:text-white tracking-tight leading-tight">
                App Messages
              </h1>
              <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400 max-w-xl leading-relaxed">
                Broadcast and targeted messages delivered via the client
                heartbeat - scoped global, by provider domain, or to a single
                LunaKey™.
              </p>
            </div>
          </div>
          <div className="sm:flex-shrink-0 sm:ml-auto">
            {isPreview ? (
              <button
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 hover:shadow-xl hover:shadow-primary-500/40 hover:-translate-y-0.5 text-white px-5 py-3 text-sm font-semibold transition-all ring-1 ring-white/10"
                type="button"
                onClick={() => setShowUpgradeModal(true)}
              >
                <i className="fas fa-lock text-xs" />
                Upgrade to Unlock
              </button>
            ) : (
              <button
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 hover:shadow-xl hover:shadow-primary-500/40 hover:-translate-y-0.5 text-white px-5 py-3 text-sm font-semibold transition-all ring-1 ring-white/10"
                type="button"
                onClick={() => setShowSendModal(true)}
              >
                <i className="fas fa-paper-plane text-xs" />
                Send Message
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filter bar - inert in preview mode */}
      <div
        aria-disabled={isPreview}
        className={`bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-3 sm:p-5 mb-4 ${
          isPreview ? "opacity-60 pointer-events-none select-none" : ""
        }`}
      >
        <div className="flex flex-wrap items-center gap-x-3 sm:gap-x-5 gap-y-2.5">
          {isAdmin && (
            <div className="flex items-center gap-2 sm:gap-2.5">
              <i
                aria-label="Origin"
                className="fas fa-user-tag text-xs text-gray-400 dark:text-gray-500"
                title="Origin"
              />
              <span className="hidden sm:inline text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-[0.1em]">
                Origin
              </span>
              <div
                className="inline-flex items-center gap-0.5 p-1 rounded-xl bg-gray-100 dark:bg-gray-900/50 ring-1 ring-gray-200 dark:ring-gray-700 overflow-x-auto max-w-full"
                role="group"
              >
                {ORIGIN_PILLS.map((p) => {
                  const active = origin === p.key;

                  return (
                    <button
                      aria-pressed={active}
                      className={`whitespace-nowrap px-2.5 sm:px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        active
                          ? "bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-300 shadow-sm ring-1 ring-gray-200 dark:ring-gray-600"
                          : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                      }`}
                      key={p.key}
                      type="button"
                      onClick={() => setOrigin(p.key)}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 sm:gap-2.5">
            <i
              aria-label="Severity"
              className="fas fa-bell text-xs text-gray-400 dark:text-gray-500"
              title="Severity"
            />
            <span className="hidden sm:inline text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-[0.1em]">
              Severity
            </span>
            <div
              className="inline-flex items-center gap-0.5 p-1 rounded-xl bg-gray-100 dark:bg-gray-900/50 ring-1 ring-gray-200 dark:ring-gray-700 overflow-x-auto max-w-full"
              role="group"
            >
              {SEVERITY_PILLS.map((p) => {
                const active = severity === p.key;

                return (
                  <button
                    aria-pressed={active}
                    className={`inline-flex items-center gap-1.5 whitespace-nowrap px-2.5 sm:px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      active
                        ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm ring-1 ring-gray-200 dark:ring-gray-600"
                        : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                    }`}
                    disabled={isPreview}
                    key={p.key}
                    type="button"
                    onClick={() => setSeverity(p.key)}
                  >
                    {p.dot && (
                      <span className={`w-1.5 h-1.5 rounded-full ${p.dot}`} />
                    )}
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-2.5">
            <i
              aria-label="Status"
              className="fas fa-circle-dot text-xs text-gray-400 dark:text-gray-500"
              title="Status"
            />
            <span className="hidden sm:inline text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-[0.1em]">
              Status
            </span>
            <div className="relative">
              <select
                className="appearance-none text-xs font-medium rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/50 text-gray-900 dark:text-white pl-3 pr-8 py-2 focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 focus:outline-none transition-colors cursor-pointer"
                disabled={isPreview}
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <i className="fas fa-chevron-down text-[10px] text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>

          <div className="relative w-full sm:flex-1 sm:w-auto sm:min-w-[220px] sm:ml-auto">
            <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
            <input
              className="w-full text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 text-gray-900 dark:text-white placeholder-gray-400 pl-9 pr-9 py-2 focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 focus:bg-white dark:focus:bg-gray-900/80 focus:outline-none transition-all"
              disabled={isPreview}
              placeholder="Search by title..."
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                aria-label="Clear search"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                type="button"
                onClick={() => setSearch("")}
              >
                <i className="fas fa-times text-[10px]" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table - read-only marketing preview for non-subscription resellers */}
      <div
        aria-disabled={isPreview}
        className={`bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden ${
          isPreview ? "pointer-events-none select-none" : ""
        }`}
      >
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Severity
                </th>
                <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Title
                </th>
                {isAdmin && (
                  <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Published By
                  </th>
                )}
                <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Target
                </th>
                <th className="hidden lg:table-cell px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Acks
                </th>
                <th className="hidden sm:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Created
                </th>
                <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Expires
                </th>
                {isAdmin && <th className="px-3 sm:px-4 py-3 w-10" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
              {loading && (
                <tr>
                  <td
                    className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400"
                    colSpan={isAdmin ? 8 : 6}
                  >
                    <i className="fas fa-circle-notch fa-spin mr-2" />{" "}
                    Loading...
                  </td>
                </tr>
              )}
              {!loading && visibleRows.length === 0 && isPreview && (
                <tr>
                  <td
                    className="px-4 py-14 text-center"
                    colSpan={isAdmin ? 8 : 6}
                  >
                    <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center ring-1 ring-primary-500/20">
                      <i className="fas fa-lock text-primary-500 text-base" />
                    </div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      App Messages is part of the subscription reseller plan
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 max-w-sm mx-auto leading-relaxed">
                      Send broadcast and targeted messages straight to your
                      customers&apos; apps once you upgrade.
                    </p>
                  </td>
                </tr>
              )}
              {!loading && visibleRows.length === 0 && !isPreview && (
                <tr>
                  <td
                    className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400"
                    colSpan={isAdmin ? 8 : 6}
                  >
                    No messages match the current filters.
                  </td>
                </tr>
              )}
              {!loading &&
                visibleRows.map((msg) => (
                  <tr
                    className="hover:bg-gray-50 dark:hover:bg-gray-900/30"
                    key={msg.id}
                  >
                    <td className="px-3 sm:px-4 py-3 whitespace-nowrap align-top">
                      <span className={severityBadge(msg.severity)}>
                        {msg.severity}
                      </span>
                    </td>
                    <td className="px-3 sm:px-4 py-3">
                      <div
                        className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-[180px] sm:max-w-[320px]"
                        title={msg.title}
                      >
                        {msg.title}
                      </div>
                      <div
                        className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[180px] sm:max-w-[320px]"
                        title={msg.body}
                      >
                        {msg.body}
                      </div>
                      {msg.sent_by_override && (
                        <div
                          className="text-[11px] text-gray-500 dark:text-gray-400 truncate max-w-[180px] sm:max-w-[320px] mt-0.5"
                          title={msg.sent_by_override}
                        >
                          From: {msg.sent_by_override}
                        </div>
                      )}
                      {/* Mobile-only meta: surfaces hidden columns */}
                      <div className="md:hidden mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                        <span>{formatTarget(msg)}</span>
                        <span className="sm:hidden">
                          - {formatDateTime(msg.created_at)}
                        </span>
                      </div>
                    </td>
                    {isAdmin && (
                      <td className="hidden md:table-cell px-4 py-3 whitespace-nowrap align-top">
                        <span className={originBadge(msg.publishedBy?.type)}>
                          {msg.publishedBy?.type || "-"}
                        </span>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {msg.publishedBy?.displayName || "-"}
                        </div>
                      </td>
                    )}
                    <td className="hidden md:table-cell px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 align-top">
                      {formatTarget(msg)}
                    </td>
                    <td className="hidden lg:table-cell px-4 py-3 whitespace-nowrap text-right text-sm text-gray-700 dark:text-gray-300 tabular-nums align-top">
                      {msg.ackCount ?? 0}
                    </td>
                    <td className="hidden sm:table-cell px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 align-top">
                      {formatDateTime(msg.created_at)}
                    </td>
                    <td className="hidden lg:table-cell px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 align-top">
                      {formatDateTime(msg.expires_at)}
                    </td>
                    {isAdmin && (
                      <td className="px-3 sm:px-4 py-3 whitespace-nowrap text-right align-top">
                        <button
                          className="text-gray-400 hover:text-red-500 transition-colors p-1"
                          title="Delete message"
                          type="button"
                          onClick={() => setDeleteTarget(msg)}
                        >
                          <i className="fas fa-trash" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && pagination.total > 0 && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 sm:px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <div className="text-xs text-gray-500 dark:text-gray-400 text-center sm:text-left">
              Showing {(pagination.page - 1) * pagination.limit + 1}-
              {Math.min(pagination.page * pagination.limit, pagination.total)}{" "}
              of {pagination.total}
            </div>
            <div className="flex items-center justify-center gap-2">
              <button
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                disabled={pagination.page <= 1}
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <i className="fas fa-chevron-left text-xs mr-1" /> Prev
              </button>
              <span className="text-sm text-gray-700 dark:text-gray-300 tabular-nums">
                {pagination.page} / {pagination.pages}
              </span>
              <button
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                disabled={pagination.page >= pagination.pages}
                type="button"
                onClick={() =>
                  setPage((p) => Math.min(pagination.pages, p + 1))
                }
              >
                Next <i className="fas fa-chevron-right text-xs ml-1" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Send Message modal - never mounted in preview mode */}
      {!isPreview && (
        <SendAppMessageModal
          isOpen={showSendModal}
          onClose={() => setShowSendModal(false)}
          onPublished={handlePublished}
        />
      )}

      {/* Subscription upgrade pitch - shown on entry for preview resellers */}
      <Transition appear as={Fragment} show={showUpgradeModal}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setShowUpgradeModal(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 shadow-2xl ring-1 ring-gray-200 dark:ring-white/5 transition-all">
                  <div className="px-7 pt-7 pb-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center flex-shrink-0 shadow-lg shadow-primary-500/30 ring-1 ring-white/10">
                        <i className="fas fa-bullhorn text-white text-base" />
                      </div>
                      <Dialog.Title className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white tracking-tight">
                        Unlock App Messages
                      </Dialog.Title>
                    </div>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                      {UPGRADE_MODAL_COPY}
                    </p>
                  </div>
                  <div className="px-7 py-4 border-t border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-900/30 rounded-b-2xl flex flex-col sm:flex-row sm:justify-end gap-2">
                    <button
                      className="px-5 py-2.5 text-sm font-medium rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                      type="button"
                      onClick={() => setShowUpgradeModal(false)}
                    >
                      Maybe Later
                    </button>
                    <button
                      className="px-5 py-2.5 text-sm font-medium rounded-xl border border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-colors"
                      type="button"
                      onClick={() => navigate("/reseller-plans")}
                    >
                      <i className="fas fa-box-open mr-2 text-xs" />
                      View Plans
                    </button>
                    <button
                      className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 text-white hover:shadow-lg hover:shadow-primary-500/30 hover:-translate-y-0.5 transition-all"
                      type="button"
                      onClick={() => navigate(RESELLER_SUBSCRIPTION_TICKET_URL)}
                    >
                      <i className="fas fa-headset text-xs" />
                      Create Support Ticket
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Delete confirmation */}
      <Transition appear as={Fragment} show={!!deleteTarget}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => !deleteInFlight && setDeleteTarget(null)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 shadow-2xl ring-1 ring-gray-200 dark:ring-white/5 transition-all">
                  <div className="px-7 pt-7 pb-5">
                    <div className="w-12 h-12 rounded-2xl bg-red-50 dark:bg-red-500/10 flex items-center justify-center mb-4 ring-1 ring-red-500/20">
                      <i className="fas fa-trash text-red-500 text-base" />
                    </div>
                    <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white tracking-tight">
                      Delete this message?
                    </Dialog.Title>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                      &ldquo;{deleteTarget?.title}&rdquo; will be removed
                      immediately. Acknowledgement receipts cascade with it.
                      This cannot be undone.
                    </p>
                  </div>
                  <div className="px-7 py-4 border-t border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-900/30 rounded-b-2xl flex justify-end gap-2">
                    <button
                      className="px-5 py-2.5 text-sm font-medium rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
                      disabled={deleteInFlight}
                      type="button"
                      onClick={() => setDeleteTarget(null)}
                    >
                      Cancel
                    </button>
                    <button
                      className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl bg-gradient-to-r from-red-500 to-red-600 text-white hover:shadow-lg hover:shadow-red-500/30 hover:-translate-y-0.5 disabled:opacity-50 disabled:shadow-none disabled:translate-y-0 transition-all"
                      disabled={deleteInFlight}
                      type="button"
                      onClick={handleConfirmDelete}
                    >
                      {deleteInFlight ? (
                        <>
                          <i className="fas fa-circle-notch fa-spin text-xs" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-trash text-xs" />
                          Delete
                        </>
                      )}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default AppMessages;
